import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import Button from '@panneau/element-button';
import TextField from '@panneau/field-text';
import Fuse from 'fuse.js';

var styles = {"container":"panneau-field-autocomplete-container","list":"panneau-field-autocomplete-list","focused":"panneau-field-autocomplete-focused","btn":"panneau-field-autocomplete-btn","thumbnail":"panneau-field-autocomplete-thumbnail","item":"panneau-field-autocomplete-item","input":"panneau-field-autocomplete-input","open":"panneau-field-autocomplete-open","empty":"panneau-field-autocomplete-empty","clear":"panneau-field-autocomplete-clear","withClear":"panneau-field-autocomplete-withClear"};

var propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })]),
  textValue: PropTypes.string,
  searchOptions: PropTypes.shape({
    isCaseSensitive: PropTypes.bool,
    includeScore: PropTypes.bool,
    includeMatches: PropTypes.bool,
    minMatchCharLength: PropTypes.number,
    shouldSort: PropTypes.bool,
    threshold: PropTypes.number,
    distance: PropTypes.number
  }),
  maxResults: PropTypes.number,
  disabled: PropTypes.bool,
  showEmpty: PropTypes.bool,
  withoutClear: PropTypes.bool,
  withoutMatch: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onTextChange: PropTypes.func,
  children: PropTypes.node
};
var defaultProps = {
  items: [],
  value: null,
  textValue: null,
  searchOptions: {
    // Search in `label` and in `value` items in an object array
    keys: ['label', 'value']
  },
  maxResults: 10,
  disabled: false,
  showEmpty: true,
  placeholder: null,
  withoutClear: false,
  withoutMatch: false,
  className: null,
  onFocus: null,
  onBlur: null,
  onChange: null,
  onTextChange: null,
  children: null
};
var AutocompleteField = function AutocompleteField(_ref) {
  var providedItems = _ref.items,
    providedValue = _ref.value,
    providedTextValue = _ref.textValue,
    searchOptions = _ref.searchOptions,
    maxResults = _ref.maxResults,
    disabled = _ref.disabled,
    showEmpty = _ref.showEmpty,
    placeholder = _ref.placeholder,
    withoutClear = _ref.withoutClear,
    withoutMatch = _ref.withoutMatch,
    className = _ref.className,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    onChange = _ref.onChange,
    onTextChange = _ref.onTextChange,
    children = _ref.children;
  var fuse = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    focused = _useState4[0],
    setFocused = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showListIcon = _useState6[0],
    setShowListIcon = _useState6[1];
  var _ref2 = isObject(providedValue) ? providedValue : {},
    _ref2$label = _ref2.label,
    partialValue = _ref2$label === void 0 ? null : _ref2$label;
  var value = isString(providedValue) ? providedValue : partialValue || null;
  var _useState7 = useState(providedTextValue),
    _useState8 = _slicedToArray(_useState7, 2),
    textValue = _useState8[0],
    setTextValue = _useState8[1];
  var items = providedItems || [];
  useEffect(function () {
    var options = _objectSpread({
      isCaseSensitive: false,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 1,
      shouldSort: true
    }, searchOptions);
    fuse.current = !withoutMatch ? new Fuse(items, options) : null;
  }, [items, searchOptions, withoutMatch]);
  var list = useMemo(function () {
    return textValue && fuse.current !== null ? fuse.current.search(textValue) : items.map(function (item) {
      return {
        item: item
      };
    });
  },
  // Wrapped to match fuse results
  [textValue, items]);
  var maxedList = maxResults !== null && maxResults > 0 ? list.slice(0, maxResults) : list;
  var _onClick = useCallback(function (e, newValue) {
    e.preventDefault();
    onChange(newValue);
    var _ref3 = newValue || {},
      label = _ref3.label;
    if (label !== null) {
      setTextValue(label);
    }
    setOpen(false);
  }, [list, onChange, setOpen, setTextValue]);
  var onFieldFocus = useCallback(function () {
    setShowListIcon(true);
    setOpen(true);
    setFocused(true);
    if (onFocus !== null) {
      onFocus();
    }
  }, [onFocus, setOpen, setFocused, setShowListIcon]);
  var onFieldBlur = useCallback(function () {
    setFocused(false);
    if (onBlur !== null) {
      onBlur();
    }
  }, [onBlur, setOpen, setFocused, setShowListIcon]);
  var onToggleOpen = useCallback(function () {
    setOpen(!open);
  }, [open, setOpen]);
  var onClear = useCallback(function (e) {
    e.stopPropagation();
    setTextValue(null);
    setOpen(false);
  }, [setTextValue]);
  var onInputChange = useCallback(function (val) {
    if (onTextChange !== null) {
      onTextChange(val);
    } else if (setTextValue !== null) {
      setTextValue(val);
    }
    if (val) {
      setOpen(true);
    } else {
      setOpen(showEmpty);
    }
  }, [setTextValue, setOpen, onTextChange, showEmpty]);
  var listClassNames = classNames([styles.list, 'border', 'rounded', 'rounded-top-0', _defineProperty({}, styles.focused, focused)]);
  var listItems = children !== null ? /*#__PURE__*/React.createElement("div", {
    className: listClassNames
  }, children) : /*#__PURE__*/React.createElement("div", {
    className: listClassNames
  }, /*#__PURE__*/React.createElement("ul", {
    className: "list-group list-group-flush"
  }, maxedList.map(function (_ref5) {
    var item = _ref5.item;
    var _ref6 = item || {},
      _ref6$label = _ref6.label,
      label = _ref6$label === void 0 ? null : _ref6$label,
      _ref6$image = _ref6.image,
      image = _ref6$image === void 0 ? null : _ref6$image,
      _ref6$value = _ref6.value,
      itemValue = _ref6$value === void 0 ? null : _ref6$value;
    var _ref7 = image || {},
      imageUrl = _ref7.url,
      imageThumbnailUrl = _ref7.thumbnailUrl;
    var finalImageUrl = imageThumbnailUrl || imageUrl || null;
    return /*#__PURE__*/React.createElement("li", {
      className: classNames(['list-group-item', styles.item]),
      key: "auto-".concat(item.label)
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: classNames(['btn', 'd-flex', 'align-items-center', 'border-0', styles.btn, {
        'color-primary': itemValue === value
      }]),
      "data-value": item.value,
      onClick: function onClick(e) {
        return _onClick(e, item);
      }
    }, finalImageUrl !== null ? /*#__PURE__*/React.createElement("img", {
      src: finalImageUrl,
      className: classNames([styles.thumbnail, 'img-fluid']),
      alt: label
    }) : null, label));
  })));
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, styles.withClearButton, !withoutClear), className, className !== null)])
  }, /*#__PURE__*/React.createElement(TextField, {
    className: classNames([styles.input, _defineProperty(_defineProperty({}, styles.open, open), styles.empty, maxedList.length < 1)]),
    value: textValue,
    placeholder: placeholder,
    onChange: onInputChange,
    disabled: disabled,
    onFocus: onFieldFocus,
    onBlur: onFieldBlur
  }), open && maxedList.length > 0 && !disabled ? listItems : null, !withoutClear && !disabled && value !== null ? /*#__PURE__*/React.createElement(Button, {
    className: styles.clear,
    onClick: onClear,
    icon: "x-circle"
  }) : null, showListIcon && maxedList.length > 0 && !disabled && value === null ? /*#__PURE__*/React.createElement(Button, {
    className: styles.clear,
    onClick: onToggleOpen,
    icon: open ? 'caret-up' : 'caret-down'
  }) : null);
};
AutocompleteField.propTypes = propTypes;
AutocompleteField.defaultProps = defaultProps;

var definition = {
  id: 'autocomplete',
  component: 'Autocomplete'
};

export { AutocompleteField as default, definition };
